import { OnDestroy } from '@angular/core';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MenuController } from '@ionic/angular';
import { BasePage } from 'src/app/core/base-page';
import { SelectOption } from 'src/app/core/component/form-inputs/select-input/select-input.component';
import { AppSettings } from 'src/app/core/configs/app.settings';
import { BrowserHelper } from 'src/app/core/helper/browser.helper';
import { MapUtilities } from 'src/app/core/helper/map-utils';
import { ServiceHelper } from 'src/app/core/helper/services.helper';
import { Dictionary, Utilities } from 'src/app/core/helper/util';
import { Log } from 'src/app/core/log';
import { CommunitySubscription } from 'src/app/data/schema/cbs/community-subscription';
import { Community, CommunityCreatedSource } from 'src/app/data/schema/community';
import { CommunityService } from 'src/app/data/service/community.service';
import { OrgType, RefTableService, TemplateCommunity } from 'src/app/data/service/ref-table.service';

interface TemplateFeatures {
  name: string;
  description: string;
  selected: boolean;
  imgPath: string;
}
@Component({
  selector: 'app-new-community',
  templateUrl: './new-community.page.html',
  styleUrls: ['./new-community.page.scss'],
})
export class NewCommunityPage extends BasePage implements OnInit, OnDestroy {

  newCommunity: Community = new Community();

  title: string = 'Create Your Community!';

  orgTypes: OrgType[] = [];
  orgTypeOptions: SelectOption[] = [];
  orgTypeNameSelected: string;

  templateCommunities: TemplateCommunity[] = [];
  selectedTemplateId: number;
  selectedTemplate: TemplateCommunity;

  fullTemplates: TemplateCommunity[] = [];
  narrowTemplates: TemplateCommunity[] = [];

  currentStep = 1;
  stepLabels = ['Personal Details', 'Account Details'];
  isDestroyed: boolean = false;

  // is basically always null since creating new community
  activeSubscription: CommunitySubscription;

  get isLoggedIn() { return ServiceHelper.authSvc.token != null }

  features: Dictionary<TemplateFeatures> = {
    Events: { name: 'Events', description: 'Sell tickets and manage attendees', imgPath: '', selected: false },
    Shop: { name: 'Shop', description: 'Online shop with categories and products', imgPath: '', selected: false },
    Sponsors: { name: 'Sponsors', description: 'Sponsors, categorized and with optional tiers', imgPath: '', selected: false },
    Static: { name: 'Static Pages', description: 'Static web pages to build out an online presence, like About Us, Our Team, etc.', imgPath: '', selected: false },
    Fundraising: { name: 'Fundraising Campaign', description: 'A fundraising campaign with donation items, event, sponsors and an auction', imgPath: '', selected: false },
    News: { name: 'News/Posts', description: 'A news feed to keep your community members informed and up to date', imgPath: '', selected: false },
    // Newsletters: {name: 'Newsletters', description: 'Create regular newsletters to send out to your community members to keep them informed', imgPath:'', selected:false},
    Resources: { name: 'Resources', description: 'Online resources, articles and document uploads', imgPath: '', selected: false },
    // Memberships: {name: 'Memberships', description: 'Online resources and document uploads', imgPath:'', selected:false},
  }
  includedFeatures: TemplateFeatures[] = [];
  excludedFeatures: TemplateFeatures[] = [];

  constructor(
    activatedRoute: ActivatedRoute,
    private menuCtrl: MenuController,
    private refTableSvc: RefTableService,
    private communityService: CommunityService) {
    super();
    activatedRoute.params.subscribe(params => {
      if (params['id'] != null) {
        this.existingCommunity(+params['id']);
      }
    });

    Log.debug('new community wizard...')
  }

  async existingCommunity(id: number) {
    if (Community.activeCommunity != null && id == Community.activeCommunity.communityId) {
      this.newCommunity = Community.activeCommunity;
      this.currentStep = 3;
      this.title = 'Complete Your Community Setup: ' + Community.activeCommunity.name
    } else if (Community.activeCommunity == null) {
      this.newCommunity = await this.communityService.loadCommunity(id);
      if (this.newCommunity != null) {
        this.currentStep = 3;
        this.title = 'Complete Your Community Setup: ' + Community.activeCommunity.name
      } else {
        this.newCommunity = new Community();
        this.currentStep = 1;
      }
    }
  }

  async ngOnInit() {
    await this.menuCtrl.enable(false);
    if (ServiceHelper.authSvc.token != null) {
      const myCommunities = await Community.getMyCommunities();
      if (myCommunities.length > 0) {
        Log.debug('menu enabled')
        this.menuCtrl.enable(true);
      }
    }

    this.orgTypes = await this.refTableSvc.getOrgTypes();
    this.orgTypes.forEach(c => {
      if ( c.deleted == false ) {
        this.orgTypeOptions.push({ value: c.orgTypeId, displayValue: c.name });
      }
    });

    this.templateCommunities = await this.refTableSvc.getTemplateCommunities();
    this.fullTemplates = this.templateCommunities.filter(t => { return t.isFull == true });
    this.narrowTemplates = this.templateCommunities.filter(t => { return t.isFull != true });

    var mapInput = document.getElementById('org-map-input') as HTMLInputElement;
    while (mapInput == null && !this.isDestroyed) {
      await Utilities.wait(333);
      mapInput = document.getElementById('org-map-input') as HTMLInputElement;
    }
    if (mapInput != null) {
      await MapUtilities.initMapsAutoComplete('org-map-input', false,
        (addr, place) => this.addrSelected(addr, place));
    }
  }

  async canDeactivate() {
    // if user hasn't selected a template, have them confirm they are starting from scratch
    if ( this.newCommunity.communityId > 0 && this.newCommunity.templatesAdded == null ) {
      if ( await ServiceHelper.confirmSomething('Start from Scratch?',
      'Currently you can only copy from a template community when your community is first created. You can always create a new community if you change your mind.',
      'Cancel', 'Start from Scratch') ) {
        this.selectedTemplateId = 0;
        this.skipTemplates();
        return false;
      }
      return false; 
    }
    return true;
  }

  override ngOnDestroy(): void {
    Log.debug('menu enabled')
    if (ServiceHelper.authSvc.token != null) {
      this.menuCtrl.enable(true);
    }
    this.isDestroyed == true;
  }

  addrSelected(addr: string, place: any) {
    Log.debug('Address selected, place = ', place)
    // called when address is selected
    this.newCommunity.address1 = addr;
    this.newCommunity.city = place.city;
    this.newCommunity.state = place.state;
    this.newCommunity.country = place.countryCode;
  }

  orgTypeSelected() {
    const selectedOrg = this.orgTypes.find(o => { return o.orgTypeId == this.newCommunity.orgTypeId });
    if (selectedOrg == null) {
      this.newCommunity.otherOrgType = undefined;
      this.orgTypeNameSelected = undefined
      return;
    }
    this.templateSelected(selectedOrg.templateCommunityId);
    this.orgTypeNameSelected = selectedOrg.name;
    if (this.orgTypeNameSelected != 'Other') {
      this.newCommunity.otherOrgType = undefined;
    }
  }

  templateSelected(templateId: number) {
    this.selectedTemplateId = templateId;
    this.selectedTemplate = this.templateCommunities.find(t => { return t.templateCommunityId == this.selectedTemplateId });

    // and also rebuild the included/excluded features arrays accordingly
    this.includedFeatures = [];
    this.excludedFeatures = [];
    for (let f of Object.values(this.features)) {
      f.selected = false;
    }
    if (this.selectedTemplate.hasEvents) { this.features['Events'].selected = true; this.includedFeatures.push(this.features['Events']) }
    else { this.excludedFeatures.push(this.features['Events']); }

    if (this.selectedTemplate.hasShop) { this.features['Shop'].selected = true; this.includedFeatures.push(this.features['Shop']) }
    else { this.excludedFeatures.push(this.features['Shop']); }

    if (this.selectedTemplate.hasSponsors) { this.features['Sponsors'].selected = true; this.includedFeatures.push(this.features['Sponsors']) }
    else { this.excludedFeatures.push(this.features['Sponsors']); }

    if (this.selectedTemplate.hasStatic) { this.features['Static'].selected = true; this.includedFeatures.push(this.features['Static']) }
    else { this.excludedFeatures.push(this.features['Static']); }

    if (this.selectedTemplate.hasNews) { this.features['News'].selected = true; this.includedFeatures.push(this.features['News']) }
    else { this.excludedFeatures.push(this.features['News']); }

    if (this.selectedTemplate.hasResources) { this.features['Resources'].selected = true; this.includedFeatures.push(this.features['Resources']) }
    else { this.excludedFeatures.push(this.features['Resources']); }

    // if ( selectedTemplate.hasNewsletters) {this.features['Newsletters'].selected = true; this.includedFeatures.push(this.features['Newsletters'])}
    // else {this.excludedFeatures.push(this.features['Newsletters']);}

    if (this.selectedTemplate.hasFundraising) { this.features['Fundraising'].selected = true; this.includedFeatures.push(this.features['Fundraising']) }
    else { this.excludedFeatures.push(this.features['Fundraising']); }
  }

  open(communityUrl: string) {
    const clientRootUrl = AppSettings.clientRootUrl;
    const url = String.isNullOrWhiteSpace(clientRootUrl) ? `https://${communityUrl}` :
      `${clientRootUrl}/${communityUrl}`;
    BrowserHelper.staticOpenUrl(url);
  }

  formValid() {
    if (this.currentStep == 1) {
      return !Utilities.stringEmpty(this.newCommunity.name) &&
        this.newCommunity.name.length > 3 &&
        this.newCommunity.country != null &&
        (
          (this.newCommunity.country != 'CA' && this.newCommunity.country != 'US') ||
          this.newCommunity.state != null
        ) &&
        this.orgTypeNameSelected != null &&
        (this.orgTypeNameSelected != 'Other' || !Utilities.stringEmpty(this.newCommunity.otherOrgType));
    }
    return true;
  }

  countrySelected() {
    // clear out in case was previously selected
    this.newCommunity.state = undefined;
  }

  loginSucceeded() {
    if (this.currentStep == 2) {
      this.onNext();
    }
  }

  async skipTemplates() {
    // not selecting a template and copying samples, just go straight to subscription page
    this.currentStep = 6;
    this.selectedTemplateId = 0;
    this.newCommunity.templatesAdded = '0';

    // save with scratch template, should try to save this silently
    this.newCommunity.save(null, null, null, true);

    Community.activeCommunity.templatesAdded = '0';
    
    this.animateStep('fade-in-animation-from-right');
  }

  async onNext() {

    // as soon as the user has an account, create their community so 
    // we can detect abandonment from template selection
    if ((this.currentStep == 1 && this.isLoggedIn) || this.currentStep == 2) {
      // Create the community as soon as they have an account to do so
      Log.debug('Create community...')
      await this.createCommunity();

      this.currentStep = 3;
      this.animateStep('fade-in-animation-from-right');

      this.title = `Load Sample Content Into '${this.newCommunity.name}'`;
    }
    //
    // step 3 is template selection, nothing to do until they check the features
    //
    //
    // step 4 is feature selection
    //
    // step 5 is confirmation where we actually copy the template
    else if (this.currentStep == 3) {
      Log.debug('Copy template...')

      await this.copyTemplate();

      this.currentStep = 6;
      this.animateStep('fade-in-animation-from-right');

      this.title = `Choose Your Subscription for '${this.newCommunity.name}'`;
    }
    else if (this.currentStep == 6) {
      // finally, after subscription selection, we can move on.

      // if we selected no template, lets save the community with template 0 added so we 
      // stop showing this wizard
      if (this.selectedTemplateId == 0 && this.newCommunity.templatesAdded == null) {
        this.newCommunity.templatesAdded = '0';
        if (await this.newCommunity.save(null, null, null, true)) {
          Community.activeCommunity.templatesAdded = '0'; // since it is cached and might keep taking us to this page
        }
      }

      // depending on if we came from SSO or not, we may need to send users to a specific page
      const ssoPath = localStorage.getItem('ssoPath');
      localStorage.removeItem('ssoPath');

      if (String.isNullOrWhiteSpace(ssoPath)) {
        // No SSO, just take them to main dashboard which will have helpul text on how to get started
        await ServiceHelper.navCtrl.navigateRoot('/engagement/dashboard');
      } else if (ssoPath === 'marketplace') {
        await ServiceHelper.navCtrl.navigateRoot('/marketplace/dash?src=create');
      } else if (ssoPath === 'create') {
        await ServiceHelper.navCtrl.navigateRoot('/fundraising/auctions?src=create');
      }

    }
    else {
      this.currentStep++;
      this.animateStep('fade-in-animation-from-right');
    }
  }

  onPrev(step?: number) {
    if (step != null) {
      this.currentStep = step;
    } else {
      if (this.currentStep == 3 && this.isLoggedIn) {
        this.currentStep = 1;
      } else {
        this.currentStep--;
      }
    }
    this.animateStep('fade-in-animation-from-left');
  }

  animateStep(anim: string) {
    // make sure new div starts transparent and trigger animation
    const stepDiv = document.getElementById(this.currentStep + '');
    if (stepDiv != null) {
      stepDiv.classList.add('pre-anim')
      stepDiv.classList.add(anim)
      setTimeout(() => {
        stepDiv.classList.remove('pre-anim')
      }, 600);
    }
  }

  private async createCommunity() {
    // Convert name to subdomain, no special characters, hyphens in place of spaces
    this.newCommunity.communityUrl =
      this.newCommunity.name.replace(/[^a-zA-Z0-9\s]/g, "")
        .toLowerCase()
        .replace(/\s+/g, "-");
    this.newCommunity.communityUrl += '.communibee.ca'
    this.newCommunity.createdFrom = CommunityCreatedSource.CbsDashboard;

    this.newCommunity.contactEmail = ServiceHelper.authSvc.profile.username;
    this.newCommunity.contactName = ServiceHelper.authSvc.profile.firstName + ' ' + ServiceHelper.authSvc.profile.lastName;

    if (await this.newCommunity.save(this.pageState)) {
      // navigate user to community setup screens
      // show a modal with some intro messaging on what to do next

      localStorage.setItem('curr_community', this.newCommunity.communityId.toString());
      Community.clearCached();
      await this.communityService.loadCommunity(this.newCommunity.communityId);

      await ServiceHelper.displayAlert('Congratulations!', 'Your community has been successfully created!');
    }
  }
  
  async copySpecificCommunity() {
    try {
      const params = {
        copyEvents: true,
        copySponsors: true,
        copyMarketPlace: true,
        copyStaticPages: true,
        copyNews: true,
        copyResources: true,
        copyNewsLetters: true,
        copyFundraising: true,
      };
      this.newCommunity.templatesAdded = '1249';
      await Community.activeCommunity.copyTemplate(1249, params);
    } finally {
    }
  }

  private async copyTemplate() {
    const copying = await ServiceHelper.displayLoad('Copying samples, this can take a couple minutes, please be patient!');


    const selectedFeatures = this.includedFeatures.filter(f => { return f.selected == true })
      .map(f => { return f.name });
    
      Log.debug(selectedFeatures);

    try {
      const params = {
        copyEvents: selectedFeatures.includes('Events'),
        copySponsors: selectedFeatures.includes('Sponsors'),
        copyMarketPlace: selectedFeatures.includes('Shop'),
        copyStaticPages: selectedFeatures.includes('Static Pages'),
        copyNews: selectedFeatures.includes('News/Posts'),
        copyResources: selectedFeatures.includes('Resources'),
        copyNewsLetters: selectedFeatures.includes('Newsletters'),
        copyFundraising: selectedFeatures.includes('Fundraising Campaign'),
      };
      this.newCommunity.templatesAdded = this.selectedTemplate.communityId.toString();
      await Community.activeCommunity.copyTemplate(this.selectedTemplate.communityId, params);
    } finally {
      copying.dismiss();
    }
  }
}
